<template>
    <div>
        <div v-if="loading" class="mt-2">
            <content-placeholders>
                <content-placeholders-text :lines="3" />
            </content-placeholders>
        </div>
        <div v-else class="mt-2">
            <div
                class="alert text-center mt-2"
                :style="colors.getColor(worksheet_color)"
            >
                Printable Header Color
            </div>
            <b-form-select
                v-model="worksheet_color"
                class="mb-2"
                :options="colorOptions"
                @change="updateWorksheetHeaderColor"
                :disabled="disabled || !can_update_printable"
            />
            <div
                v-if="publication_worksheets && publication_worksheets.length"
                class="border-left pl-3"
            >
                <draggable
                    v-model="publication_worksheets"
                    handle='.drag-handle'
                    @end="handleDragEnd"
                    :disabled="disabled || !can_update_printable"
                >
                    <div
                        v-for="(worksheet, index) in publication_worksheets"
                        :key="index"
                        class="worksheet drag-handle"
                    >
                        <WorksheetsListItem
                            :publication="publication"
                            :worksheet="worksheet"
                            @update="getWorksheets"
                            @unlink="unlinkWorksheet"
                            @getPrintables="getWorksheets"
                            :disabled="disabled || !can_update_printable"
                        />
                    </div>
                </draggable>
            </div>

            <b-alert
                variant="danger"
                :show="!publication_worksheets || publication_worksheets && !publication_worksheets.length"
                class="mb-2"
            >
                No Printables added
            </b-alert>

            <div class="d-flex mt-2">
                <div class="ml-auto">
                    <b-input-group class="pr-3" size="sm">
                        <b-form-input
                            placeholder="Printable ID"
                            v-model="quickAddPrintableId"
                            :disabled="disabled || !can_add_printable"
                        />
                        <b-input-group-append>
                            <b-button
                                variant="outline-success"
                                @click="quickAddPrintable"
                                :disabled="disabled || !can_add_printable"
                            >
                                Quick add
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                </div>
                <div>
                    <b-dropdown
                        size="sm"
                        variant="outline-primary"
                        dropup
                        right
                        :disabled="disabled"
                    >
                        <template #button-content>
                            <font-awesome-icon :icon="['fad', 'plus-square']" />
                            Add Printable
                        </template>
                        <b-dropdown-item
                            @click="createWorksheet"
                            :disabled="disabled || !can_add_printable"
                        >
                            <font-awesome-icon
                                :icon="['fad', 'plus-square']"
                                class="text-success"
                                fixed-width
                            />
                            Create Printable
                        </b-dropdown-item>
                        <b-dropdown-item
                            @click="show_attach_worksheets_modal = true"
                            :disabled="disabled || !can_add_printable"
                        >
                            <font-awesome-icon
                                :icon="['fad', 'plus-square']"
                                class="text-primary"
                                fixed-width
                            />
                            Add Existing Printable
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
        </div>
        <attach-worksheets-modal
            v-model="show_attach_worksheets_modal"
            :article_worksheets="publication_worksheets"
            @create="createWorksheet"
            @link="linkWorksheet"
            @unlink="unlinkWorksheet"
            @hide="show_attach_worksheets_modal = false"
        />
    </div>
</template>

<style lang="scss" scoped>
.worksheet + .worksheet {
    margin-top: 0.5rem;
}
</style>

<script>
import WorksheetsListItem from '@/components/worksheet/WorksheetsListItem'
import AttachWorksheetsModal from '@/components/modals/article/worksheets/AttachWorksheetsModal'
import colors from '@/common/colors'
import draggable from 'vuedraggable'
import {mapState} from 'vuex'
import moment from 'moment/moment'

export default {
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        colors() {
            return colors
        },
        worksheet_color: {
            get() {
                if (this.article) {
                    return this.article.worksheet_color
                } else if (this.unit) {
                    return this.unit.pivot ? this.unit.pivot.worksheet_color : null
                } else if (this.unit_group) {
                    return this.unit_group.worksheet_color
                }
            },
            set(value) {
                if (this.article) {
                    this.article.worksheet_color = value
                } else if (this.unit) {
                    if (!this.unit.pivot) {
                        this.$set(this.unit, 'pivot', {})
                    }
                    this.unit.pivot.worksheet_color = value
                } else if (this.unit_group) {
                    this.unit_group.worksheet_color = value
                }
            }
        },
        can_update_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_printable')
        },
        can_add_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'add_printable')
        },
    },
    data: () => ({
        loading: false,
        worksheets: [],
        show_attach_worksheets_modal: false,
        publication_worksheets: null,
        quickAddPrintableId: null,
        colorOptions: [
            {value: null, text: 'Select Worksheet Header Color'},
            {value: 'green', text: 'Green'},
            {value: 'blue', text: 'Blue'},
            {value: 'orange', text: 'Orange'},
            {value: 'red', text: 'Red'},
        ],
    }),
    props: [
        'article',
        'unit',
        'unit_group',
        'publication',
        'disabled',
    ],
    components: {
        AttachWorksheetsModal,
        WorksheetsListItem,
        draggable,
    },
    methods: {
        async getWorksheets() {
            try {
                this.loading = true

                let response = null
                if (this.article) {
                    response = await this.axios.get(`/api/articles/worksheets/${this.article.id}`)
                } else if (this.unit) {
                    // this should have been a get
                    response = await this.axios.post(`/api/units/worksheets/${this.unit.id}`)
                } else if (this.unit_group) {
                    response = await this.axios.get(`/api/unit_groups/worksheets/${this.unit_group.id}`)
                } else {
                    throw new Error('Get Worksheets: No article, unit or unit group set')
                }

                if (response && response.data) {
                    this.worksheets = _.orderBy(response.data, 'pivot.order_number')
                }
                this.publication_worksheets = _.filter(this.worksheets, worksheet => {
                    if (
                        !worksheet.pivot ||
                        (worksheet.pivot && worksheet.pivot.publication_id == this.publication.id)
                    ) {
                        return worksheet
                    }
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to load printables`,
                    },
                    time: moment().toISOString(),
                })
                console.error(e.message)
            } finally {
                this.loading = false
            }
        },
        async createWorksheet() {
            try {
                if (this.article) {
                    await this.axios.post(`/api/articles/create_worksheet/${this.article.id}`, {
                        publication_id: this.publication.id,
                    })
                } else if (this.unit) {
                    await this.axios.post(`/api/units/create_worksheet/${this.unit.id}`, {
                        publication_id: this.publication.id,
                    })
                } else if (this.unit_group) {
                    await this.axios.post(`/api/unit_groups/create_worksheet/${this.unit_group.id}`, {
                        publication_id: this.publication.id,
                    })
                } else {
                    throw new Error('Create printable: No article, unit or unit group set')
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to create printable`,
                    },
                    time: moment().toISOString(),
                })
                console.error(e.message)
            } finally {
                await this.getWorksheets()
            }
        },
        quickAddPrintable() {
            this.linkWorksheet({id: this.quickAddPrintableId})
            this.quickAddPrintableId = null
        },
        async linkWorksheet(worksheet) {
            try {
                if (this.article) {
                    await this.axios.post(`/api/articles/add_worksheet/${this.article.id}`, {
                        worksheet_id: worksheet.id,
                        publication_id: this.publication.id,
                        unit_id: this.unit.id
                    })
                } else if (this.unit) {
                    await this.axios.post(`/api/units/add_worksheet/${this.unit.id}`, {
                        worksheet_id: worksheet.id,
                        publication_id: this.publication.id,
                    })
                } else if (this.unit_group) {
                    await this.axios.post(`/api/unit_groups/add_worksheet/${this.unit_group.id}`, {
                        worksheet_id: worksheet.id,
                        publication_id: this.publication.id
                    })
                } else {
                    throw new Error('Link printable: No article, unit or unit group set')
                }

            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: 'Failed to link printable',
                    },
                    time: moment().toISOString(),
                })
                console.error(e.message)
            } finally {
                await this.getWorksheets()
            }

        },
        async unlinkWorksheet(worksheet) {
            const result = await this.$swal({
                html: 'Are you sure you want to unlink this printable?',
                showCancelButton: true,
                confirmButtonText: 'Unlink Printable',
                confirmButtonColor: '#E67E22',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })

            if (!result.value) {
                return
            }

            try {
                if (this.article) {
                    await this.axios.post(`/api/articles/remove_worksheet/${this.article.id}`, {
                        worksheet_id: worksheet.id,
                        publication_id: this.publication.id,
                        unit_id: this.unit.id
                    })
                } else if (this.unit) {
                    await this.axios.post(`/api/units/remove_worksheet/${this.unit.id}`, {
                        worksheet_id: worksheet.id,
                        publication_id: this.publication.id,
                    })
                } else if (this.unit_group) {
                    await this.axios.post(`/api/unit_groups/remove_worksheet/${this.unit_group.id}`, {
                        worksheet_id: worksheet.id,
                        publication_id: this.publication.id,
                    })
                } else {
                    throw new Error('Unlink printable: No article, unit or unit group set')
                }

            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to unlink printable`,
                    },
                    time: moment().toISOString(),
                })
                console.error(e.message)
            } finally {
                await this.getWorksheets()
            }
        },
        async handleDragEnd() {
            if (this.disabled) {
                return
            }

            const updatedWorksheets = this.publication_worksheets.map((media, index) => {
                return {...media, worksheet_order: index + 1}
            })

            try {
                if (this.article) {
                    await this.axios.post('/api/articles/reorder_worksheets', {
                        new_worksheet_order: updatedWorksheets,
                    })
                } else if (this.unit) {
                    // Despite the name, this is for worksheets.
                    await this.axios.post('/api/units/reorder_panels', {
                        new_panel_order: updatedWorksheets,
                    })
                } else if (this.unit_group) {
                    await this.axios.post('/api/unit_groups/reorder_worksheets', {
                        new_worksheet_order: updatedWorksheets,
                    })
                } else {
                    throw new Error('Reorder printable: No article, unit or unit group set')
                }

            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to reorder printable`,
                    },
                    time: moment().toISOString(),
                })
                console.error(e.message)
            } finally {
                await this.getWorksheets()
            }
        },
        async updateWorksheetHeaderColor(color) {
            if (!color) {
                return
            }

            try {
                if (this.article) {
                    await this.axios.post(`/api/articles/update_worksheet_header_color/${this.article.id}`, {
                        worksheet_color: color,
                        unit_id: this.unit.id,
                    })
                } else if (this.unit) {
                    await this.axios.post(`/api/units/update_worksheet_header_color/${this.unit.id}`, {
                        worksheet_color: color,
                        unit_group_id: this.unit_group.id,
                    })
                } else if (this.unit_group) {
                    await this.axios.post(`/api/unit_groups/update_worksheet_header_color/${this.unit_group.id}`, {
                        worksheet_color: color,
                    })
                } else {
                    throw new Error('Update printable header color: No article, unit or unit group set')
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update printable header color`,
                    },
                    time: moment().toISOString(),
                })
                console.error(e.message)
            }
        },
    },
    watch: {
        'article.worksheets_visible': {
            handler(visible) {
                if (visible) {
                    this.getWorksheets()
                }
            },
            deep: true
        },
        'unit.worksheets_visible': {
            handler(visible) {
                if (visible) {
                    this.getWorksheets()
                }
            },
            deep: true
        },
        'unit_group.worksheets_visible': {
            handler(visible) {
                if (visible) {
                    this.getWorksheets()
                }
            },
            deep: true
        }
    },
    mounted: function () {
        const articleVisible = this.article && this.article.worksheets_visible
        const unitVisible = this.unit && this.unit.worksheets_visible
        const unitGroupVisible = this.unit_group && this.unit_group.worksheets_visible
        if (articleVisible || unitVisible || unitGroupVisible) {
            this.getWorksheets()
        }
    }
}
</script>
